<script setup>
// DeckCoverCard — the library/topics deck card. A 16:9 cover (generated art when
// the deck has one, else a category-tinted gradient) with an HTML overlay:
// category chip, title, tagline, a "N Questions" badge, difficulty, a Practise
// button, and gamification slots (★ rating / plays / mastery) that render only
// when that data is present. The art is a background; everything readable is
// overlaid, so counts and stats stay live without regenerating the image.
import { computed } from "vue";

const props = defineProps({
  deck: { type: Object, required: true },
  color: { type: String, default: "#5e677c" }
});
const emit = defineEmits(["play"]);

const cover = computed(() => props.deck.cover || null);
const hasCover = computed(() => Boolean(cover.value?.imageUrl));

const prettify = (s) => String(s || "").split("-").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
const cat = computed(() => (Array.isArray(props.deck.category) ? props.deck.category : []));
const SOURCE_LABEL = { library: "My Deck", public: "Public", file: "Built-in" };
const domain = computed(() => (cat.value[0] ? prettify(cat.value[0]) : (SOURCE_LABEL[props.deck.source] || "Deck")));
const subject = computed(() => {
  if (cat.value.length > 1) return prettify(cat.value[1]);
  if (cat.value.length === 1) return prettify(cat.value[0]);
  return props.deck.area || "";
});
const tagline = computed(() => cover.value?.tagline || props.deck.description || "");

const diffLabel = computed(() => {
  const ds = Array.isArray(props.deck.difficulties) ? props.deck.difficulties : [];
  if (!ds.length) return null;
  const max = Math.max(...ds);
  return max <= 2 ? "Beginner" : max === 3 ? "Intermediate" : "Advanced";
});

// Gamification — rendered only when present (wired as that data lands).
const rating = computed(() => (typeof props.deck.rating === "number" ? props.deck.rating : null));
const plays = computed(() => props.deck.plays ?? null);
const mastery = computed(() => (props.deck.masteredCount != null && props.deck.total ? { done: props.deck.masteredCount, total: props.deck.total } : null));
const masteryPct = computed(() => (mastery.value ? Math.round((mastery.value.done / mastery.value.total) * 100) : 0));

// Category glyph — a real Bootstrap Icon (the font is already loaded app-wide).
// These used to be five hand-drawn SVG paths picked by a regex on the DOMAIN
// only, which is why they read as meaningless: "Driver Knowledge" matched
// nothing and fell back to a generic book, while "First Aid" matched /first/ and
// got a heartbeat. Match on the deepest node first (subject/topic carry the real
// meaning — "Driver Knowledge", "First Aid"), then the domain, then fall back.
const ICON_RULES = [
  [/first.?aid|cpr|drsabcd|bleed|burn|resus/, "bi-bandaid"],
  [/cardio|heart|circulat|pulse/, "bi-heart-pulse"],
  [/lung|respir|breath/, "bi-lungs"],
  [/anatom|human.?body|physiolog/, "bi-activity"],
  [/health|medic|clinic|nurs|pharma|patient/, "bi-heart-pulse"],
  [/driver|driving|road|traffic|vehicle|motor/, "bi-car-front"],
  [/sign|intersection|junction/, "bi-signpost-2"],
  [/geo|world|continent|capital|river|mountain|landmark|country|map/, "bi-globe-americas"],
  [/tech|program|comput|code|software|data|cyber/, "bi-cpu"],
  [/agri|nature|wildlife|sheep|livestock|animal|farm|plant/, "bi-tree"],
  [/water|hydrat|fluid/, "bi-droplet-half"],
  [/librar|general|misc|demo/, "bi-journal-bookmark"]
];
const iconClass = computed(() => {
  // deepest → shallowest: topic, subject, domain, then the legacy flat domain.
  const haystack = [...cat.value].reverse().concat(props.deck.domain || "", props.deck.area || "")
    .filter(Boolean).join(" ").toLowerCase();
  for (const [re, icon] of ICON_RULES) if (re.test(haystack)) return icon;
  return "bi-mortarboard"; // a topic we don't recognise is still something to learn
});

const bgStyle = computed(() => (hasCover.value
  ? { backgroundImage: `url('${cover.value.imageUrl}')` }
  : { background: `linear-gradient(118deg, color-mix(in srgb, ${props.color} 45%, #0a1526) 0%, color-mix(in srgb, ${props.color} 80%, #0a1526) 68%, color-mix(in srgb, ${props.color} 92%, #16233d) 100%)` }));
</script>

<template>
  <article class="dc" :class="{ 'no-photo': !hasCover }" :style="bgStyle" @click="emit('play')">
    <div v-if="hasCover" class="dc-scrim"></div>
    <div class="dc-ov">
      <div class="dc-cat">
        <span class="dc-ic"><i class="bi" :class="iconClass"></i></span>
        <span class="dc-div"></span>
        <span class="dc-cat-t"><b>{{ domain }}</b><i v-if="subject">{{ subject }}</i></span>
        <!-- Difficulty sits top-right: it's a label, not a live stat, and it frees
             the footer for the badge + CTA on a narrow card. -->
        <span v-if="diffLabel" class="dc-pill" :class="'d-' + diffLabel.toLowerCase()">{{ diffLabel }}</span>
      </div>

      <div class="dc-mid">
        <h3 class="dc-title" :title="deck.topic">{{ deck.topic }}</h3>
        <span class="dc-rule"></span>
        <p v-if="tagline" class="dc-tag">{{ tagline }}</p>
      </div>

      <div class="dc-foot">
        <div v-if="rating || plays || mastery" class="dc-live">
          <span v-if="rating" class="dc-stat"><span class="dc-star">★</span><b>{{ rating.toFixed(1) }}</b></span>
          <span v-if="plays" class="dc-stat"><span class="dc-pic">▶</span><b>{{ plays }}</b></span>
          <span v-if="mastery" class="dc-stat dc-mast"><span class="dc-bar"><span :style="{ width: masteryPct + '%' }"></span></span><b>{{ mastery.done }}/{{ mastery.total }}</b></span>
        </div>
        <div class="dc-cta">
          <div class="dc-badge"><span class="dc-q">?</span><span class="dc-q-t"><b>{{ deck.itemCount }} Question{{ deck.itemCount === 1 ? "" : "s" }}</b><small>Test your knowledge</small></span></div>
          <button class="dc-play" @click.stop="emit('play')"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg><span>Practise</span></button>
        </div>
      </div>
    </div>
  </article>
</template>

<style scoped>
/* 16:9 is a MINIMUM, not a fixed height. `aspect-ratio` + `overflow:hidden` used
   to CLIP the card: a title that wrapped to 3 lines pushed the footer out of the
   box and it was silently cut off. A spacer pseudo-element sets the 16:9 floor
   while the overlay (in the same grid cell) is free to make the card taller when
   the text needs it — so nothing is ever truncated. */
.dc {
  container-type: inline-size;
  position: relative; display: grid; border-radius: 14px; overflow: hidden; cursor: pointer;
  background-size: cover; background-position: right center; background-color: #0b1730;
  box-shadow: 0 8px 22px rgba(6,12,28,.22), 0 1px 4px rgba(6,12,28,.18);
  transition: transform .14s ease, box-shadow .14s ease; isolation: isolate;
}
.dc::before { content: ""; grid-area: 1 / 1; padding-top: 56.25%; } /* the 16:9 floor */
.dc:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(6,12,28,.32), 0 2px 6px rgba(6,12,28,.24); }
.dc:focus-within { outline: 2px solid #e8b54d; outline-offset: 2px; }
.dc-scrim { grid-area: 1 / 1; z-index: 1;
  background: linear-gradient(102deg, rgba(6,11,26,.9) 0%, rgba(6,11,26,.8) 26%, rgba(6,11,26,.48) 46%, rgba(6,11,26,.1) 60%, transparent 72%); }

/* Shares the grid cell with the 16:9 spacer, so it can outgrow it (see .dc).
   Vertical padding trimmed (6.2cqi → 2.6cqi) to buy the headroom a wrapping
   title needs before the card has to grow at all. */
.dc-ov { grid-area: 1 / 1; z-index: 2; display: flex; flex-direction: column; justify-content: space-between;
  padding: 3.6cqi 6.5cqi 3.4cqi; color: #fff; }

.dc-cat { display: flex; align-items: center; gap: 2.2cqi; }
/* Difficulty pill, pinned top-right of the header row. */
.dc-cat .dc-pill { margin-left: auto; flex: none; }
.dc-ic { width: 6.6cqi; height: 6.6cqi; min-width: 22px; display: grid; place-items: center; border-radius: 50%; border: 1.5px solid rgba(255,255,255,.5); flex: none; }
.dc-ic .bi { font-size: 3.6cqi; line-height: 1; }
.dc-div { width: 1px; height: 5.4cqi; background: rgba(255,255,255,.38); }
.dc-cat-t { display: flex; flex-direction: column; line-height: 1.16; min-width: 0; }
.dc-cat-t b { color: #e8b54d; font-size: 2.5cqi; font-weight: 800; letter-spacing: .11em; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dc-cat-t i { color: #fff; font-size: 2.5cqi; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; font-style: normal; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dc-mid { margin-top: auto; margin-bottom: 2.2cqi; min-width: 0; }
.dc-title { margin: 0; font-weight: 800; letter-spacing: -.02em; line-height: 1.02; font-size: 8.4cqi;
  text-shadow: 0 2px 16px rgba(0,0,0,.35); display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.dc-rule { display: block; width: 11cqi; height: .8cqi; min-height: 3px; background: #e8b54d; border-radius: 2px; margin: 2.2cqi 0 1.9cqi; }
/* 48cqi forced the tagline to wrap into the art even when there was room; 70cqi
   lets it use the width the scrim already covers. */
.dc-tag { margin: 0; font-style: italic; color: #d3dcec; font-size: 2.9cqi; line-height: 1.3; max-width: 70cqi;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

.dc-foot { display: flex; flex-direction: column; gap: 2.6cqi; }
.dc-live { display: flex; flex-wrap: wrap; align-items: center; gap: 1.8cqi 3.4cqi; }
.dc-stat { display: inline-flex; align-items: center; gap: 1.1cqi; font-size: 2.8cqi; color: #eaf0fa; font-variant-numeric: tabular-nums; }
.dc-star { color: #e8b54d; font-size: 3.2cqi; line-height: 1; }
.dc-pic { color: #f0cd7e; font-size: 2.4cqi; }
.dc-mast .dc-bar { width: 11cqi; height: 1.3cqi; min-height: 4px; border-radius: 99px; background: rgba(255,255,255,.22); overflow: hidden; display: inline-block; }
.dc-mast .dc-bar span { display: block; height: 100%; background: linear-gradient(90deg, #e8b54d, #f2cd7d); }
/* The pill sits top-RIGHT, which is the one corner the scrim deliberately leaves
   clear so the art shows through — so it can't borrow contrast from the gradient
   the way the rest of the overlay does. Against a bright sky or a white pill
   bottle the old translucent-white fill let the text vanish. It carries its own
   dark backdrop instead: opaque enough to be legible on ANY cover, blurred so it
   still reads as glass rather than a sticker. */
.dc-pill { font-size: 2.3cqi; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  padding: .9cqi 2.4cqi; border-radius: 999px;
  background: rgba(7,14,30,.72); border: 1px solid rgba(255,255,255,.28);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  text-shadow: 0 1px 3px rgba(0,0,0,.55); box-shadow: 0 2px 8px rgba(4,9,20,.35); }
.dc-pill.d-beginner { color: #9ff0bd; } .dc-pill.d-intermediate { color: #ffdf85; } .dc-pill.d-advanced { color: #ffb3a3; }

.dc-cta { display: flex; align-items: center; gap: 2.6cqi; flex-wrap: wrap; }
.dc-badge { display: inline-flex; align-items: center; gap: 2.2cqi; background: rgba(7,14,30,.6); border: 1px solid rgba(255,255,255,.14); border-radius: 11px; padding: 1.8cqi 2.9cqi; }
.dc-q { width: 6cqi; height: 6cqi; min-width: 20px; display: grid; place-items: center; border-radius: 50%; border: 1.5px solid #e8b54d; color: #e8b54d; font-weight: 800; font-size: 3.4cqi; }
.dc-q-t { display: flex; flex-direction: column; line-height: 1.14; }
.dc-q-t b { font-size: 3.1cqi; font-weight: 700; }
.dc-q-t small { font-size: 2.3cqi; color: #cdd8ea; }
.dc-play { display: inline-flex; align-items: center; gap: 1.5cqi; font: inherit; font-size: 3cqi; font-weight: 700; color: #fff; background: #5e677c; border: 0; border-radius: 9px; padding: 2.2cqi 3.6cqi; cursor: pointer; }
.dc-play svg { width: 3.4cqi; height: 3.4cqi; }
.dc-play:hover { filter: brightness(1.09); }
.dc-play:focus-visible { outline: 2px solid #e8b54d; outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) { .dc { transition: none; } }
</style>
